import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./portal_bman.css";

import IconLogout from "./res/Icon-Logout.svg";
import IconVerifyAuth from "./res/Icon-VerifyAuth.svg";
import IconDashboard from "./res/Icon-Dashboard.svg";
import IconBackup from "./res/Icon-Backup.svg";
import IconServiceConnection from "./res/Icon-Seviceconnection.svg";
import IconArchives from "./res/Icon-ArchivesandFolders.svg";
import BManLogo from "./res/BManLogo.svg";
import IconAgent from "./res/Icon-Agent.svg";
import IconSubscription from "./res/Icon-Subscription.svg";

import Dashboard from "./dashboard";
import BackupJobs from "./backupjobs";
import VerifyAuthentication from "./verifyauthentication";
import ServiceConnection from "./serviceconnection";
import ArchivesAndFolders from "./archivesandfolders";
import AgentManagement from "./agentmanagement";
import Subscription from "./subscription";

import { useAuth } from "../../context/AuthContext";
import { API_BASE_URL } from "../../config/apiConfig";

const PortalBman = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  
  const btnColRef = useRef(null);
  const [leftShift, setLeftShift] = useState(0);
  const [collapsed, setCollapsed] = useState(false);
  const [clock, setClock] = useState({ time: "", date: "" });

  // ✅ default view is now "dashboard"
  const [activeView, setActiveView] = useState("dashboard");

  // LEFT PANEL: measure button column and auto-collapse on load
  useEffect(() => {
    if (btnColRef.current) {
      const offset = btnColRef.current.offsetLeft || 0;
      setLeftShift(offset);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => setCollapsed(true));
      });
    }
  }, []);

  // CLOCK: update every minute
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const hh = String(now.getHours()).padStart(2, "0");
      const mm = String(now.getMinutes()).padStart(2, "0");
      const d = now.getDate();
      const m = now.getMonth() + 1;
      const y = now.getFullYear();

      setClock({
        time: `${hh}:${mm}`,
        date: `${d}/${m}/${y}`,
      });
    };

    updateTime();
    const interval = setInterval(updateTime, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleToggle = useCallback(() => {
    setCollapsed((prev) => !prev);
  }, []);

  // ✅ Logout handler
  const handleLogout = useCallback(async () => {
    try {
      // Call backend logout endpoint
      await fetch(`${API_BASE_URL}/logout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
    } catch (err) {
      console.error("Logout API error:", err);
    }
    
    // Clear local auth state and redirect
    if (logout) {
      logout();
    }
    navigate("/login?suite=1");
  }, [logout, navigate]);

  // ✅ nav handlers
  const goDashboard = useCallback(() => setActiveView("dashboard"), []);
  const goAgent = useCallback(() => setActiveView("agent"), []);
  const goBackupJobs = useCallback(() => setActiveView("backupjobs"), []);
  const goVerifyAuth = useCallback(() => setActiveView("verifyauth"), []);
  const goServiceConnection = useCallback(() => setActiveView("serviceconnection"), []);
  const goArchives = useCallback(() => setActiveView("archives"), []);
  const goSubscription = useCallback(() => setActiveView("subscription"), []);

  const renderRightPanel = () => {
    switch (activeView) {
      case "agent":
        return <AgentManagement />;
      case "backupjobs":
        return <BackupJobs />;
      case "verifyauth":
        return <VerifyAuthentication />;
      case "serviceconnection":
        return <ServiceConnection />;
      case "archives":
        return <ArchivesAndFolders />;
      case "subscription":
        return <Subscription />;
      case "dashboard":
      default:
        return <Dashboard />;
    }
  };

  return (
    <div className="container01">
      {/* LEFT PANEL */}
      <div
        className={`container01-left ${collapsed ? "collapsed" : ""}`}
        style={{ "--left-shift": `${leftShift}px` }}
      >
        <div className="left-column-items">
          <div className="items-top">
            <div
              className="left-item logo-item"
              onClick={goDashboard}
              style={{ cursor: "pointer" }}
            >
              <img src={BManLogo} alt="BMan Logo" className="logo-icon" />
            </div>
          </div>

          <div className="items-mid">
            {/* ✅ Dashboard */}
            <div className="left-item" onClick={goDashboard} style={{ cursor: "pointer" }}>
              <div className="item-row">
                <div className="item-image">
                  <img src={IconDashboard} alt="Dashboard" className="leftmenu-icon" />
                </div>
                <div className="item-description">
                  <span
                    className={`dash-label ${
                      activeView === "dashboard" ? "active-underline" : ""
                    }`}
                  >
                    Dashboard
                  </span>
                </div>
              </div>
            </div>

            {/* ✅ Agent */}
            <div
              className="left-item"
              onClick={goAgent}
              style={{ cursor: "pointer" }}
            >
              <div className="item-row">
                <div className="item-image">
                  <img src={IconAgent} alt="Agent" className="leftmenu-icon" />
                </div>
                <div className="item-description">
                  <span
                    className={`dash-label ${
                      activeView === "agent" ? "active-underline" : ""
                    }`}
                  >
                    Agent
                  </span>
                </div>
              </div>
            </div>

            {/* ✅ Backup Jobs */}
            <div className="left-item" onClick={goBackupJobs} style={{ cursor: "pointer" }}>
              <div className="item-row">
                <div className="item-image">
                  <img src={IconBackup} alt="Backup Jobs" className="leftmenu-icon" />
                </div>
                <div className="item-description">
                  <span
                    className={`dash-label ${
                      activeView === "backupjobs" ? "active-underline" : ""
                    }`}
                  >
                    Backup Jobs
                  </span>
                </div>
              </div>
            </div>

            {/* ✅ Verify Authentication */}
            <div className="left-item" onClick={goVerifyAuth} style={{ cursor: "pointer" }}>
              <div className="item-row">
                <div className="item-image">
                  <img
                    src={IconVerifyAuth}
                    alt="Verify Authentication"
                    className="leftmenu-icon"
                  />
                </div>
                <div className="item-description">
                  <span
                    className={`dash-label ${
                      activeView === "verifyauth" ? "active-underline" : ""
                    }`}
                  >
                    Verify Authentication
                  </span>
                </div>
              </div>
            </div>

            {/* ✅ Service Connection */}
            <div
              className="left-item"
              onClick={goServiceConnection}
              style={{ cursor: "pointer" }}
            >
              <div className="item-row">
                <div className="item-image">
                  <img
                    src={IconServiceConnection}
                    alt="Service Connection"
                    className="leftmenu-icon"
                  />
                </div>
                <div className="item-description">
                  <span
                    className={`dash-label ${
                      activeView === "serviceconnection" ? "active-underline" : ""
                    }`}
                  >
                    Service Connection
                  </span>
                </div>
              </div>
            </div>

            {/* ✅ Archives & Folders */}
            <div className="left-item" onClick={goArchives} style={{ cursor: "pointer" }}>
              <div className="item-row">
                <div className="item-image">
                  <img
                    src={IconArchives}
                    alt="Archives and Folders"
                    className="leftmenu-icon"
                  />
                </div>
                <div className="item-description">
                  <span
                    className={`dash-label ${
                      activeView === "archives" ? "active-underline" : ""
                    }`}
                  >
                    Archives and Folders
                  </span>
                </div>
              </div>
            </div>

            {/* ✅ Subscription */}
            <div className="left-item" onClick={goSubscription} style={{ cursor: "pointer" }}>
              <div className="item-row">
                <div className="item-image">
                  <img
                    src={IconSubscription}
                    alt="Subscription"
                    className="leftmenu-icon"
                  />
                </div>
                <div className="item-description">
                  <span
                    className={`dash-label ${
                      activeView === "subscription" ? "active-underline" : ""
                    }`}
                  >
                    Subscription
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom section (User + Version) */}
          <div className="item-bottom">
            <div className="left-item item7-container">
              <div className="item7-cell item7-username">{user?.username || "Username"}</div>
              <div className="item7-cell item7-version">v 0.1</div>
            </div>
          </div>
        </div>

        <div className="left-column-button" ref={btnColRef}>
          <div className="collapse-symbol" onClick={handleToggle}>
            {collapsed ? ">>" : "<<"}
          </div>

          <div className="left-column-spacer" />

          <div className="left-column-bottom-group">
            <div className="clock-cell">
              <div className="clock-text">
                <div className="clock-time">{clock.time}</div>
                <div className="clock-date">{clock.date}</div>
              </div>
            </div>

            {/* ✅ Logout button with onClick handler */}
            <div 
              className="logoutimage" 
              title="Logout"
              onClick={handleLogout}
              style={{ cursor: "pointer" }}
            >
              <img src={IconLogout} alt="Logout" className="logoutimage-icon" />
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT PANEL container stays the same */}
      <div
        className="container01-right"
        style={{
          marginLeft: collapsed ? -leftShift : 0,
        }}
      >
        <div className="right-inner">
          <div className="right-outer">{renderRightPanel()}</div>
        </div>
      </div>
    </div>
  );
};

export default PortalBman;
